"use client";

import { useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import {
  calculateLalKitabKundli,
  type LalKitabKundliRequest,
  type LalKitabKundliResponse,
} from "@/lib/api/lal-kitab";
import { trackCalculatorUsed } from "@/lib/analytics";
import { ResultPanel } from "./_sections";

export default function LalKitabKundliClient() {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LalKitabKundliResponse | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data: LalKitabKundliRequest = {
        name: name.trim(),
        birth_date: birthDate,
        birth_place: birthPlace.trim(),
        ...(birthTime ? { birth_time: birthTime } : {}),
      };
      const response = await calculateLalKitabKundli(data);
      setResult(response);
      trackCalculatorUsed({ calculator: "lal-kitab-kundli" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <LalKitabHeader />

        {!result && (
          <LalKitabForm
            name={name}
            birthDate={birthDate}
            birthTime={birthTime}
            birthPlace={birthPlace}
            loading={loading}
            error={error}
            onNameChange={setName}
            onBirthDateChange={setBirthDate}
            onBirthTimeChange={setBirthTime}
            onBirthPlaceChange={setBirthPlace}
            onSubmit={handleSubmit}
          />
        )}

        {result && (
          <LalKitabResult result={result} resultRef={resultRef} onReset={() => setResult(null)} />
        )}
      </div>
    </div>
  );
}

function LalKitabHeader() {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        लाल किताब · Lal Kitab
      </p>
      <h2
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Free Lal Kitab Kundli Calculator
      </h2>
      <p className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto text-ish-text-muted">
        Enter your birth details to get your Lal Kitab chart — planet positions by house, active
        upay (remedies), and ancestral debts (rin). 100% free, engine-grounded analysis.
      </p>
      <div
        className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium"
        style={{ color: "var(--ish-text-faint)" }}
      >
        <TrustSignal icon="shield" label="Pt. Roop Chand Joshi System" />
        <TrustSignal icon="clock" label="Instant Results" />
        <TrustSignal icon="user" label="100% Free" />
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}

function LalKitabForm({
  name,
  birthDate,
  birthTime,
  birthPlace,
  loading,
  error,
  onNameChange,
  onBirthDateChange,
  onBirthTimeChange,
  onBirthPlaceChange,
  onSubmit,
}: {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  loading: boolean;
  error: string | null;
  onNameChange: (value: string) => void;
  onBirthDateChange: (value: string) => void;
  onBirthTimeChange: (value: string) => void;
  onBirthPlaceChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <div className="mx-auto max-w-md">
      <form onSubmit={onSubmit} className="card-sacred p-6 sm:p-8 space-y-5">
        <LalKitabInput
          id="lk-name"
          label="Full Name"
          type="text"
          value={name}
          onChange={onNameChange}
          placeholder="e.g. Rahul Sharma"
          required
        />
        <LalKitabInput
          id="lk-date"
          label="Date of Birth"
          type="date"
          value={birthDate}
          onChange={onBirthDateChange}
          max={new Date().toISOString().slice(0, 10)}
          required
        />
        <LalKitabInput
          id="lk-time"
          label={
            <>
              Birth Time{" "}
              <span className="font-normal text-[12px] text-ish-text-faint">(optional)</span>
            </>
          }
          type="time"
          value={birthTime}
          onChange={onBirthTimeChange}
        />
        <LalKitabInput
          id="lk-place"
          label="Birth Place"
          type="text"
          value={birthPlace}
          onChange={onBirthPlaceChange}
          placeholder="e.g. Delhi, India"
          required
        />
        {error && (
          <p
            className="text-[13px] rounded-lg px-3 py-2"
            style={{ background: "rgba(220,38,38,0.08)", color: "var(--ish-error, #dc2626)" }}
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg text-[15px] font-semibold transition-opacity"
          style={{
            background: "var(--ish-accent)",
            color: "#fff",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Calculating…" : "Get My Lal Kitab Kundli →"}
        </button>
      </form>
    </div>
  );
}

function LalKitabInput({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  required,
  max,
}: {
  id: string;
  label: ReactNode;
  type: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  max?: string;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[13px] font-semibold mb-1.5"
        style={{ color: "var(--ish-dark)" }}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        placeholder={placeholder}
        max={max}
        className="w-full rounded-lg px-4 py-2.5 text-[14px] outline-none focus:ring-2"
        style={{
          border: "1px solid var(--ish-border)",
          background: "var(--ish-bg-input, #fff)",
          color: "var(--ish-dark)",
        }}
      />
    </div>
  );
}

function LalKitabResult({
  result,
  resultRef,
  onReset,
}: {
  result: LalKitabKundliResponse;
  resultRef: RefObject<HTMLDivElement | null>;
  onReset: () => void;
}) {
  return (
    <div ref={resultRef}>
      <div className="flex items-center justify-between mb-6">
        <h2
          className="text-[20px] font-bold"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          {result.name}&apos;s Lal Kitab Analysis
        </h2>
        <button onClick={onReset} className="text-[13px] underline text-ish-text-muted">
          Calculate Again
        </button>
      </div>
      <ResultPanel result={result} />
    </div>
  );
}
